import React from "react";
import { cls } from "cls";
import { PlusLink } from "@clarkinc/zest-design-system/ui";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { ProductDetailPageResult } from "~/ProductDetails/ProductDetailPageResult";
import getLowestDisplayPrice from "~/common/Product/getLowestDisplayPrice";
import { User } from "~/common/Store/models/User";
import SpecificationField from "./SpecificationField";
import Ribbon from "~/common/Ribbon/primitive";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";

interface Props {
  product: ProductDetailPageResult;
  user: User;
}

const Compare = ({ product, user }: Props) => {
  const { specifications } = product.specificationTable;
  const products = product.expandedDescription.compareProducts?.products || [];

  if (!products?.length) {
    return null;
  }

  const initialClassificationOrder =
    product.expandedDescription.compareProducts?.comparableClassificationIds;

  const isPlusEligible =
    product.isWebstaurantPlusEligible && product.hasFreeShipping;
  const isWebPlusUser = user.hasWebPlus && product.isWebstaurantPlusEligible;

  const plusClass = isPlusEligible && !isWebPlusUser ? "pr-0.5" : "";

  // Get all comparable specs from all products
  // some products may have specs which are comparable but don't exist on others
  const comparableSpecs = products.reduce(
    (prev, next) => {
      return [
        ...prev,
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...next.specificationTable?.specifications.filter(
          (specification) =>
            // only take specs which are comparable and aren't already accounted for
            product.expandedDescription.compareProducts?.comparableClassificationIds.includes(
              specification.id,
            ) &&
            !prev
              .map((prevSpecfication) => prevSpecfication.id)
              .includes(specification.id),
        ),
      ];
    },
    [
      ...specifications.filter((specification) =>
        product.expandedDescription.compareProducts?.comparableClassificationIds.includes(
          specification.id,
        ),
      ),
    ],
  );

  if (initialClassificationOrder) {
    comparableSpecs.sort(
      (x, y) =>
        initialClassificationOrder.indexOf(x.id) -
        initialClassificationOrder.indexOf(y.id),
    );
  }

  return (
    <div
      id="compare"
      className="detail-section compare-template ag-flex"
      style={{ scrollMarginTop: "100px" }}
    >
      <h2 id="compare-to-other-products-heading">Compare to Other Products</h2>
      <section
        className="carousel-wrap comparison-slider-wrap ag-flex"
        data-gtm-component="carousel"
        data-label="#JSStringFormat(attributes.feedId)#"
        aria-label={`Compare to Other Products. ${
          [product, ...products].length
        } products to compare`}
      >
        <div
          id="compare-to-other-products"
          className="carousel-box basic-carousel"
          data-component="carousel"
        >
          <ul
            className={cls(
              "compare-header",
              "flex",
              "sr-only",
              "xs:absolute",
              "xs:w-[140px]",
              "xs:not-sr-only",
            )}
            aria-label="specifications to compare"
          >
            <div className="product-info ag-flex" />
            <li className={cls("compare-row", "h-21")}>
              <p>Price</p>
            </li>
            {comparableSpecs.map((specification) => (
              <li
                key={specification.classification}
                className={cls("compare-row", "h-21")}
              >
                <p>{specification.classification}</p>
              </li>
            ))}
          </ul>
          <div className="ag-carousel-nav">
            <button type="button" className="ag-car-prev" data-control="prev">
              <span className="sr-only">show previous carousel items</span>
            </button>
          </div>
          <div className="ag-carousel-frame" style={{ overflow: "hidden" }}>
            <div className="ag-carousel ag-full">
              {[product, ...products].map((product, index) => (
                <ul
                  aria-label={`${index === 0 ? "Current product: " : ""}${
                    product.title
                  }`}
                  className={cls(
                    "p-0",
                    "m-0",
                    "list-none",
                    "ag-item",
                    "ag-flex",
                    "gtm-product",
                    index === 0 && "base",
                  )}
                  key={product.itemNumber}
                  style={{ width: "200px" }}
                  gtm-tracking-number={product.itemNumber}
                >
                  {" "}
                  <li
                    className={cls(
                      "product-info",
                      "ag-flex",
                      "pb-[15px]",
                      "justify-center",
                    )}
                  >
                    <div className="product-image">
                      <img
                        className="lazyLoad gtm-product-image"
                        src={
                          (product.productImages?.length &&
                            product.productImages.find(
                              (productImage) =>
                                productImage.isPrimaryListingImage,
                            )?.thumbnail) ||
                          product.productImages[0]?.thumbnail ||
                          ""
                        }
                        alt=""
                      />
                    </div>
                    <span className="sub-text">{`Item #: ${product.originalId}`}</span>
                    <a
                      className={cls("ag-flex", "description", "flex-none")}
                      title={product.originalId}
                      href={product.link}
                    >
                      {product.title}
                    </a>
                    {(product.isWebstaurantPlusEligible ||
                      product.hasFreeShipping) && (
                      <div
                        className={cls(
                          "pricing",
                          "fs",
                          "pb-0",
                          "pt-1",
                          "my-0",
                          "bottom-0",
                          "flex",
                          "justify-center",
                        )}
                      >
                        {product.isWebstaurantPlusEligible && (
                          <PlusLink
                            className={`inline-flex align-middle relative ${plusClass}`}
                            iconProps={{
                              "data-testid": "compare--plus-new",
                            }}
                          />
                        )}
                        {product.hasFreeShipping &&
                          !(
                            user.hasWebPlus && product.isWebstaurantPlusEligible
                          ) && (
                            <Ribbon
                              className="box-border px-2 h-[17px] bg-blue-800"
                              data-testid="compare-free-shipping-ribbon"
                            >
                              <span className="text-xs text-white text-shadow-200 font-semibold leading-none">
                                Free Shipping
                              </span>
                            </Ribbon>
                          )}
                      </div>
                    )}
                  </li>
                  <li className={cls("compare-row", "h-21")}>
                    <div
                      className={cls(
                        "pricing",
                        "flex",
                        "flex-col",
                        "items-center",
                      )}
                    >
                      {product.price.displaySalePrice ? (
                        <SaleBadgeAndPricing
                          price={product.price.price}
                          dataTestId="compare-carousel-sale"
                          salePricingClassNames={cls(
                            "p-0",
                            "pb-1",
                            "flex",
                            "items-center",
                            "text-xs",
                            "text-gray-800",
                          )}
                          saleBadgeExtraClassNames={cls("px-1", "mr-1")}
                        />
                      ) : null}
                      <p className="price">
                        <span className="sr-only">Price:</span>
                        {product.groupingAttributes.isGroupMaster && (
                          <span className="from">From</span>
                        )}
                        {formatAsCurrency.formatIfUndefined(
                          getLowestDisplayPrice(product, user, true, true),
                        )}
                        {product.unitOfMeasure && (
                          <span className="each">{`/${product.unitOfMeasure}`}</span>
                        )}
                      </p>
                    </div>
                  </li>
                  {comparableSpecs.map((specification) => (
                    <SpecificationField
                      key={specification.id}
                      productSpecifications={
                        product.specificationTable?.specifications
                      }
                      specificationId={specification.id}
                    />
                  ))}
                </ul>
              ))}
            </div>
          </div>
          <div className="ag-carousel-nav">
            <button type="button" className="ag-car-next" data-control="next">
              <span className="sr-only">show more carousel items</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compare;
